import React, { useState, useEffect } from 'react'
import { Container, ButtonImg, ButtonText, ButtonInner, CountText, ButtonPill, IconWrapper } from './style'
import arrow from '../../assets/stock/LoadMoreButton/arrow-down.svg'
import ConfirmationModal from '../ModalConfirmation'

interface LoadMoreButtonProps {
  currentCount: number
  totalCount?: number | null
  onLoadMore: () => void
  onLoadAll?: () => void | Promise<void>
  isLoading?: boolean
  hasMore?: boolean
  label?: string
  disabled?: boolean
  buttonLabel?: string
  loadingLabel?: string
  className?: string
}

const LoadMoreButton: React.FC<LoadMoreButtonProps> = ({
  currentCount,
  totalCount = null,
  onLoadMore,
  onLoadAll,
  isLoading = false,
  hasMore = true,
  label = 'itens',
  disabled = false,
  buttonLabel = 'Carregar mais',
  loadingLabel = 'Carregando...',
  className,
}) => {
  const [clickCount, setClickCount] = useState(0)
  const [showLoadAllModal, setShowLoadAllModal] = useState(false)
  const [isAutoLoadingAll, setIsAutoLoadingAll] = useState(false)

  const showCount = typeof currentCount === 'number' || typeof totalCount === 'number'
  const countText = typeof totalCount === 'number'
    ? `Mostrando ${currentCount ?? 0} de ${totalCount} ${label}`
    : `Mostrando ${currentCount ?? 0} ${label}`
  const isButtonDisabled = disabled || isLoading || !hasMore || isAutoLoadingAll

  useEffect(() => {
    if (!isAutoLoadingAll) return
    if (!hasMore) {
      setIsAutoLoadingAll(false)
      return
    }
    if (!isLoading) {
      onLoadMore()
    }
  }, [isAutoLoadingAll, isLoading, hasMore, onLoadMore])

  const handleClick = () => {
    if (isButtonDisabled) return

    if (clickCount >= 2) {
      setShowLoadAllModal(true)
      return
    }

    setClickCount((prev) => prev + 1)
    onLoadMore()
  }

  const handleContinuePagination = () => {
    setShowLoadAllModal(false)
    setClickCount(0)
    onLoadMore()
  }

  const handleConfirmLoadAll = async () => {
    setShowLoadAllModal(false)
    setClickCount(0)
    if (onLoadAll) {
      await onLoadAll()
    } else {
      setIsAutoLoadingAll(true)
      onLoadMore()
    }
  }

  const displayLoadingLabel = isAutoLoadingAll ? 'Carregando todos os registros...' : loadingLabel

  if (!hasMore && !isAutoLoadingAll) {
    return (
      <Container className={className}>
        {showCount && <CountText>{countText}</CountText>}
      </Container>
    )
  }

  return (
    <>
      <Container className={className}>
        <ButtonPill
          type="button"
          onClick={handleClick}
          disabled={isButtonDisabled}
          aria-disabled={isButtonDisabled}
        >
          <ButtonInner>
            <IconWrapper>
              <ButtonImg src={arrow} alt="Ícone de seta para carregar mais" />
            </IconWrapper>
            <ButtonText>{isLoading || isAutoLoadingAll ? displayLoadingLabel : buttonLabel}</ButtonText>
          </ButtonInner>
        </ButtonPill>
        {showCount && <CountText>{countText}</CountText>}
      </Container>

      <ConfirmationModal
        isOpen={showLoadAllModal}
        title="Carregar todos os registros?"
        message="Deseja carregar todos os registros restantes de uma só vez na tabela?"
        maxWidth="480px"
        actions={[
          {
            label: 'Não, continuar paginação',
            variant: 'outline',
            onClick: handleContinuePagination,
          },
          {
            label: 'Sim, carregar tudo',
            variant: 'primary',
            onClick: handleConfirmLoadAll,
          },
        ]}
      />
    </>
  )
}

export default LoadMoreButton
